<template>
  <teleport to="body">
    <Transition name="pa-install">
      <!--
        CENTRÉE EN BAS, et pas dans un coin : à gauche elle recouvrait les
        dernières entrées du menu latéral, à droite elle serait tombée sur les
        messages (`AltToastContainer`). Le centre est le seul endroit du bas
        qui n'appartienne à personne.
      -->
      <div
        v-if="visible"
        class="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex justify-center px-4"
      >
        <div
          class="pointer-events-auto w-full max-w-sm rounded-xl border border-slate-200 bg-white p-4 shadow-xl dark:border-alt-dark-border dark:bg-alt-dark-surface"
          role="dialog"
          aria-labelledby="pa-install-titre"
        >
          <div class="flex items-start gap-3">
            <!-- L'icône même qu'il obtiendra : c'est ce qui dit le mieux ce
                 qu'« installer » veut dire. -->
            <img src="/icone-192.png" alt="" class="h-10 w-10 shrink-0 rounded-lg" />
            <div class="min-w-0">
              <p id="pa-install-titre" class="text-sm font-semibold text-space-indigo dark:text-altoneo-50">
                Installer Plans d'actions
              </p>
              <p class="mt-1 text-xs leading-relaxed text-slate-500 dark:text-alt-dark-muted">
                <template v-if="mode === 'invitation'">
                  Une icône sur votre bureau et une fenêtre à part, sans barre d'adresse.
                </template>
                <template v-else>
                  Sur iPhone et iPad, l'installation se fait à la main :
                  <span class="font-medium text-slate-600 dark:text-alt-dark-muted">Partager</span>
                  <span aria-hidden="true"> → </span>
                  <span class="font-medium text-slate-600 dark:text-alt-dark-muted">
                    Sur l'écran d'accueil</span
                  >.
                </template>
              </p>
            </div>
          </div>

          <div class="mt-3 flex justify-end gap-2">
            <AltButton size="sm" variant="ghost" @click="refuser">
              {{ mode === 'invitation' ? 'Plus tard' : "J'ai compris" }}
            </AltButton>
            <AltButton v-if="mode === 'invitation'" size="sm" :loading="enCours" @click="lancer">
              Installer
            </AltButton>
          </div>
        </div>
      </div>
    </Transition>
  </teleport>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, computed } from 'vue';
import { useDiscoveryStore } from '@/stores/discovery';
import {
  modeInstallation,
  proposerInstallation,
  installationEnCours,
  installer,
  refuserInstallation,
} from '@/lib/installation';

const discovery = useDiscoveryStore();
const mode = modeInstallation;
const enCours = installationEnCours;

/**
 * ON LAISSE PASSER LES PREMIÈRES SECONDES.
 *
 * L'invitation de Chrome arrive au chargement, c'est-à-dire au moment précis
 * où l'utilisateur cherche ce qu'il était venu faire. Une proposition qui
 * s'ouvre par-dessus l'écran qu'on attend se refuse par réflexe, et le refus
 * est définitif : autant la poser une fois l'écran lu.
 */
const attendu = ref(false);
let minuterie: ReturnType<typeof setTimeout> | null = null;
onMounted(() => {
  minuterie = setTimeout(() => (attendu.value = true), 6000);
});
onBeforeUnmount(() => {
  if (minuterie) clearTimeout(minuterie);
});

/*
 * PAS PENDANT LA DÉCOUVERTE. La visite guidée occupe déjà le bas de l'écran
 * avec son bandeau, et surtout elle demande de suivre des étapes : lui couper
 * la parole pour vanter une icône, c'est faire abandonner les deux.
 */
const visible = computed(() => attendu.value && proposerInstallation.value && !discovery.active);

function refuser(): void {
  refuserInstallation();
}
async function lancer(): Promise<void> {
  await installer();
}
</script>

<style scoped>
.pa-install-enter-active,
.pa-install-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.pa-install-enter-from,
.pa-install-leave-to {
  opacity: 0;
  transform: translateY(0.75rem);
}
</style>
